import { useCallback, useRef } from 'react';
import { useBlocker } from 'react-router-dom';

type Params = {
  when: boolean;
};

export const useUnsavedChangesBlocker = ({ when }: Params) => {
  const bypassRef = useRef(false);

  const shouldBlock = useCallback(() => when && !bypassRef.current, [when]);

  const blocker = useBlocker(shouldBlock);

  const isOpen = blocker.state === 'blocked';

  const allowNextNavigation = useCallback(() => {
    bypassRef.current = true;

    setTimeout(() => {
      bypassRef.current = false;
    }, 0);
  }, []);

  const cancel = useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  }, [blocker]);

  const discardAndContinue = useCallback(
    (discard: () => void) => {
      discard();
      if (blocker.state === 'blocked') {
        blocker.proceed();
      }
    },
    [blocker]
  );

  const saveAndContinue = useCallback(
    async (save: () => Promise<void>) => {
      await save();
      if (blocker.state === 'blocked') {
        blocker.proceed();
      }
    },
    [blocker]
  );

  return {
    isOpen,
    cancel,
    discardAndContinue,
    saveAndContinue,
    allowNextNavigation,
  };
};
